/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Chain, Network } from '../types/networks';
import { isValidSolanaAddress, isValidSuiAddress } from '../lib/utils/format';
import { SolanaRpcClient } from '../lib/solana/solana';
import { SOLANA_MAINNET_RPC, SOLANA_DEVNET_RPC } from '../lib/constants';
import { EmbeddedWalletService } from '../lib/embedded-wallets';
import { fetchTokenPrice } from '../data/prices';

export interface WalletBalances {
    native: number; // SOL or SUI
    usdc: number;
    hot: number;
}

export interface WalletPrices {
    native: number;
    usdc: number;
    hot: number;
}

interface WalletState {
  isConnected: boolean;
  address: string | null;
  chain: Chain;
  network: Network;
  walletName: string | null;
  balance: number; // Backward compatibility (Native balance)
  balances: WalletBalances;
  prices: WalletPrices;
  totalValueUsd: number;
  error: string | null;
}

interface WalletContextType extends WalletState {
  connect: (chain: Chain, walletName: string, network?: Network, embeddedProvider?: string, email?: string) => Promise<void>;
  disconnect: () => void;
  refreshBalance: () => Promise<void>;
  setNetwork: (network: Network) => void;
  switchChain: (chain: Chain) => void;
  signMessage: (message: string) => Promise<string | null>;
  clearError: () => void;
  isConnecting: boolean;
}

const WalletContext = createContext<WalletContextType>({
  isConnected: false,
  address: null,
  chain: 'SOL',
  network: 'mainnet',
  walletName: null,
  balance: 0,
  balances: { native: 0, usdc: 0, hot: 0 },
  prices: { native: 0, usdc: 1, hot: 0 },
  totalValueUsd: 0,
  error: null,
  connect: async () => {},
  disconnect: () => {},
  refreshBalance: async () => {},
  setNetwork: () => {},
  switchChain: () => {},
  signMessage: async () => null,
  clearError: () => {},
  isConnecting: false,
});

export const useWallet = () => useContext(WalletContext);

// Declare window interface for wallet providers
declare global {
  interface Window {
    solana?: any;
    suiWallet?: any;
    __suiet__?: any; // Add Suiet support
    solflare?: any;
  }
}

export const WalletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<WalletState>({
    isConnected: false,
    address: null,
    chain: 'SOL',
    network: 'mainnet', // Default to Mainnet for production
    walletName: null,
    balance: 0,
    balances: { native: 0, usdc: 0, hot: 0 },
    prices: { native: 0, usdc: 1, hot: 0 }, // Initial prices
    totalValueUsd: 0,
    error: null
  });
  const [isConnecting, setIsConnecting] = useState(false);

  const fetchBalancesAndPrices = async (chain: Chain, net: Network, address: string) => {
      // 1. Fetch Prices
      const nativeSymbol = chain === 'SOL' ? 'SOL' : 'SUI';
      const [nativePriceData, hotPriceData] = await Promise.all([
          fetchTokenPrice(nativeSymbol),
          fetchTokenPrice('HOT')
      ]);

      const prices = {
          native: nativePriceData.price,
          usdc: 1.0, // Stable
          hot: hotPriceData.price || 0.05
      };

      // 2. Fetch Balances
      let balances: WalletBalances = { native: 0, usdc: 0, hot: 0 };

      if (chain === 'SOL') {
          try {
              // Native SOL
              const endpoint = net === 'mainnet' ? SOLANA_MAINNET_RPC : SOLANA_DEVNET_RPC;
              const rpc = new SolanaRpcClient(endpoint);
              const lamports = await rpc.getBalance(address);
              balances.native = lamports / 1_000_000_000;

              // Mock SPL balances for demo (In prod, use getParsedTokenAccountsByOwner)
              // Deterministic mock based on address for consistency
              const seed = address.charCodeAt(0) + address.charCodeAt(address.length - 1);
              balances.usdc = (seed * 5) % 10000; 
              balances.hot = (seed * 50) % 50000;

          } catch (e) {
              console.warn("Failed to fetch real SOL balance, defaulting to mock if devnet", e);
              // Fallback for devnet testing
              balances.native = net === 'devnet' ? 10 + (Math.random() * 5) : 0;
              balances.usdc = 1000;
              balances.hot = 5000;
          }
      } else {
          // SUI Handling (Mock for now)
          const multiplier = net === 'devnet' ? 10 : 1;
          balances.native = (50 + Math.random() * 450) * multiplier;
          balances.usdc = 500 * multiplier;
          balances.hot = 2500 * multiplier;
      }

      // 3. Calculate Total USD
      const totalValueUsd = 
          (balances.native * prices.native) + 
          (balances.usdc * prices.usdc) + 
          (balances.hot * prices.hot);

      return { balances, prices, totalValueUsd };
  };

  const refreshBalance = useCallback(async (
    pChain?: Chain,
    pNetwork?: Network,
    pAddress?: string
  ) => {
    const chainToUse = pChain || state.chain;
    const networkToUse = pNetwork || state.network;
    const addressToUse = pAddress || state.address;

    if (!addressToUse) return;

    try {
      const { balances, prices, totalValueUsd } = await fetchBalancesAndPrices(chainToUse, networkToUse, addressToUse);
      setState(prev => ({
        ...prev,
        balances,
        balance: balances.native,
        prices,
        totalValueUsd,
        error: null,
      }));
    } catch (e) {
      console.error("Failed to refresh balance", e);
      setState(prev => ({ ...prev, error: "Failed to refresh balance" }));
    }
  }, [state.chain, state.network, state.address]);

  // Restore from local storage on mount
  useEffect(() => {
    const saved = localStorage.getItem('hot_wallet_state');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.chain && parsed.address && parsed.isConnected) {
             // Respect stored network preference if available, otherwise default to mainnet
             setState(prev => ({ ...prev, ...parsed, error: null, network: parsed.network || 'mainnet' }));
             // Trigger balance refresh on hydrate
             setTimeout(() => refreshBalance(parsed.chain, parsed.network || 'mainnet', parsed.address), 500);
        }
      } catch (e) {
        console.error("Failed to parse wallet state", e);
      }
    }
  }, []);

  const connect = async (chain: Chain, walletName: string, network: Network = 'mainnet', embeddedProvider?: string, email?: string) => {
    if (state.isConnected && state.chain === chain && state.walletName === walletName) {
        return;
    }

    setIsConnecting(true);
    setState(prev => ({ ...prev, error: null }));
    let realAddress: string | null = null;

    try {
        // 1. Embedded Wallet (Email/Social)
        if (walletName === 'Embedded' && embeddedProvider) {
             const session = await EmbeddedWalletService.login(embeddedProvider as any, email, chain);
             realAddress = session.walletAddress;
        }
        
        // 2. Real Solana Connection (Desktop & Mobile Injection)
        else if (chain === 'SOL') {
             // Check standard injected providers
             if (walletName === 'Phantom' && window.solana?.isPhantom) {
                 const resp = await window.solana.connect();
                 realAddress = resp.publicKey.toString();
             } else if (walletName === 'Solflare' && window.solflare) {
                 await window.solflare.connect();
                 realAddress = window.solflare.publicKey.toString();
             } else if (window.solana) {
                 // Fallback for generic mobile wallets injecting window.solana
                 const resp = await window.solana.connect();
                 realAddress = resp.publicKey.toString();
             } else {
                 // Mobile Wallet Adapter / Deep Linking Support
                 const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                 if (isMobile) {
                     const redirect = encodeURIComponent(window.location.href);
                     if (walletName === 'Phantom') {
                         window.location.href = `https://phantom.app/ul/browse/${redirect}?ref=${redirect}`;
                         return; // Redirecting, break execution
                     } else if (walletName === 'Solflare') {
                         window.location.href = `https://solflare.com/ul/v1/browse/${redirect}`;
                         return; // Redirecting
                     }
                 }
             }
        }
        
        // 3. Real Sui Connection
        else if (chain === 'SUI') {
            if (walletName === 'Sui Wallet' && window.suiWallet) {
                const hasPerms = await window.suiWallet.hasPermissions();
                if (!hasPerms) await window.suiWallet.requestPermissions();
                const accounts = await window.suiWallet.getAccounts();
                if (accounts && accounts.length > 0) realAddress = accounts[0];
            } else if (walletName === 'Suiet' && window.__suiet__) {
                const resp = await window.__suiet__.connect();
                if (resp?.data?.address) realAddress = resp.data.address;
            }
        }

        // 4. Fallback Mock Addresses (Development only / Simulator)
        if (!realAddress) {
            if (walletName !== 'Embedded') {
                console.warn("No real wallet found, using simulation.");
                await new Promise(resolve => setTimeout(resolve, 800));
                
                // Deterministic mock address based on wallet name to simulate different users
                if (chain === 'SOL') {
                    realAddress = walletName === 'Phantom' 
                        ? '7xKXtg2Mrn5S8j9L2x5S8j9L2x5S8j9L2x5S8j9L2x5S'
                        : '8zF3Simu1atedSo1f1areAddress123456789ABCDEFG'; // Valid Base58 format
                } else {
                    realAddress = '0x82a4567890123456789012345678901234567890123456789012345678901234';
                }
            }
        }
        
        if (!realAddress) throw new Error("Connection failed: No address received");

        const isValid = chain === 'SOL' ? isValidSolanaAddress(realAddress) : isValidSuiAddress(realAddress);
        if (!isValid) {
            console.error(`Invalid Address Format for ${chain}: ${realAddress}`);
            // Use mock address if invalid in dev/sim mode
            if (realAddress.includes("Mock") || realAddress.includes("Simu")) {
                 realAddress = chain === 'SOL' ? '7xKXtg2Mrn5S8j9L2x5S8j9L2x5S8j9L2x5S8j9L2x5S' : '0x82a4567890123456789012345678901234567890123456789012345678901234';
            } else {
                throw new Error(`Invalid ${chain} address received.`);
            }
        }

        const { balances, prices, totalValueUsd } = await fetchBalancesAndPrices(chain, network, realAddress);

        const newState = {
          isConnected: true,
          address: realAddress,
          chain,
          network,
          walletName,
          balance: balances.native, // legacy support
          balances,
          prices,
          totalValueUsd,
          error: null
        };

        setState(newState);
        localStorage.setItem('hot_wallet_state', JSON.stringify(newState));

    } catch (e: any) {
        console.error("Connection failed", e);
        setState(prev => ({ ...prev, error: e.message || "Failed to connect wallet" }));
    } finally {
        setIsConnecting(false);
    }
  };

  const disconnect = useCallback(() => {
    const wasSuiet = state.walletName === 'Suiet';
    setState(prev => ({
        ...prev,
        isConnected: false,
        address: null,
        walletName: null,
        balance: 0,
        balances: { native: 0, usdc: 0, hot: 0 },
        totalValueUsd: 0,
    }));
    localStorage.removeItem('hot_wallet_state');
    EmbeddedWalletService.logout();
    if (window.solana) {
        try { window.solana.disconnect(); } catch (e) {}
    }
    if (wasSuiet && window.__suiet__) {
        try { window.__suiet__.disconnect(); } catch (e) {}
    }
  }, [state.walletName]);

  const setNetwork = useCallback((network: Network) => {
    setState(prev => {
        const newState = { ...prev, network };
        localStorage.setItem('hot_wallet_state', JSON.stringify(newState));
        return newState;
    });
  }, []);

  const switchChain = useCallback((chain: Chain) => {
    disconnect();
    setState(prev => ({ ...prev, chain, network: 'mainnet' }));
  }, [disconnect]);

  const signMessage = useCallback(async (message: string): Promise<string | null> => {
    if (!state.isConnected || !state.address) {
        setState(prev => ({ ...prev, error: "Not connected" }));
        return null;
    }
    
    // Real Wallet Signing Support
    try {
        const encodedMessage = new TextEncoder().encode(message);
        
        if (state.chain === 'SOL' && window.solana) {
            const signedMessage = await window.solana.signMessage(encodedMessage, 'utf8');
            return signedMessage.signature ? signedMessage.signature.toString('hex') : "signed";
        }
        // Fallback for simulation
        console.log(`Signing message (Simulated): ${message}`);
        await new Promise(r => setTimeout(r, 800));
        return `mock-sig-for-${state.address}`;
    } catch (e: any) {
        console.error("Sign message failed", e);
        return null;
    }
  }, [state.isConnected, state.address, state.chain]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const refreshBalanceForContext = useCallback(async () => {
    if (state.isConnected && state.address) {
        await refreshBalance(state.chain, state.network, state.address);
    }
  }, [state.isConnected, state.address, state.chain, state.network, refreshBalance]);

  return (
    <WalletContext.Provider
      value={{
        ...state,
        isConnecting,
        connect,
        disconnect,
        refreshBalance: refreshBalanceForContext,
        setNetwork,
        switchChain,
        signMessage,
        clearError,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};